<template>
	<div>
		<core-layout-headline-with-tabs :tabs="tabs">
			{{ $t("achievement_details") }}
		</core-layout-headline-with-tabs>
		<core-layout-content full>
			<div class="flex xs-middle xs-center p6" v-if="isLoading">
				<j-spinner />
			</div>

			<!-- Language selector -->
			<div class="xs-4 mb4" v-if="$store.state.settings.allowedLocales">
				<div class="pr2 xs-12 fs3 lh125 fw7 pb1">{{ $t("language") }}</div>
				<core-select-locale
					class="xs-12"
					size="xsmall"
					v-model="selectedLocale"
				/>
			</div>
			<j-card v-if="achievementType" class="xs-12 bg5 r2 p4">
				<!-- Type -->
				<div class="flex">
					<div class="fw7 fs3 pb2 mr2">{{ $t("type") }}</div>
					<div class="cu2" @click="onClickEdit('type')">
						<crm-icon name="edit" color="c2" size="small" />
					</div>
				</div>
				<div class="fs3 pb2">{{ achievementType.type || "---" }}</div>

				<!-- Name -->
				<div class="flex mt4">
					<div class="fw7 fs3 pb2 mr2">{{ $t("name") }}</div>
					<div class="cu2" @click="onClickEdit('name')">
						<crm-icon name="edit" color="c2" size="small" />
					</div>
				</div>
				<div class="fs3 pb2">{{ achievementType.name || "---" }}</div>

				<!-- Description -->
				<div class="flex mt4">
					<div class="fw7 fs3 pb2 mr2">{{ $t("description") }}</div>
					<div class="cu2" @click="onClickEdit('description')">
						<crm-icon name="edit" color="c2" size="small" />
					</div>
				</div>
				<div class="fs3 pb2">
					{{ achievementType.description || "---" }}
				</div>

				<!-- Logic -->
				<div class="fw7 fs3 pb2 mr2 mt4">{{ $t("achievement_logic") }}</div>
				<achievement-logic
					:value="achievementType.logic"
					@input="onInputLogic"
				/>
			</j-card>
		</core-layout-content>

		<!-- Update modal -->
		<core-modal
			name="updateFieldModal"
			:headline="update.headline"
			:width="update.width || 500"
		>
			<div class="xs-12 p4">
				<div class="xs-12 fs3 c6 fw7 pb2">{{ $t(update.label) }}</div>
				<component :is="update.componentName" v-model="update.value" />

				<j-button
					:isLoading="isLoading"
					class="mt6"
					size="small"
					@click="update.onSubmit"
					>{{ $t("confirm") }}</j-button
				>
			</div>
		</core-modal>
	</div>
</template>

<script>
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputLongText from "@/components/JBaseInputLongText"
import JSpinner from "@/components/JSpinner"
import JCard from "@/components/JCard"
import JButton from "@/components/JButton"
import CrmIcon from "@/components/CrmIcon"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineWithTabs from "@/modules/core/components/CoreLayoutHeadlineWithTabs"
import CoreModal from "@/modules/core/components/CoreModal"
import AchievementLogic from "../components/AchievementLogic"
import CoreSelectLocale from "@/modules/core/components/CoreSelectLocale"

import { i18n } from "@/locales/setup.js"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	props: {
		id: String
	},

	components: {
		JBaseInputText,
		JBaseInputLongText,
		JCard,
		JSpinner,
		JButton,
		CrmIcon,
		CoreLayoutContent,
		CoreLayoutHeadlineWithTabs,
		CoreModal,
		AchievementLogic,
		CoreSelectLocale
	},

	data() {
		return {
			achievementType: null,
			tabs: [],
			selectedLocale: i18n.locale,
			update: {
				componentName: null,
				headline: null,
				label: null,
				value: null,
				onSubmit: null
			}
		}
	},

	methods: {
		onClickEdit(fieldName) {
			this.update.headline = this.$t("update") + " " + this.$t(fieldName)
			if (fieldName === "name") {
				this.update.label = this.$t(fieldName)
				this.update.componentName = "j-base-input-text"
				this.update.onSubmit = () => this.upsertL10n(fieldName)
				this.update.value = this.achievementType.name
			}
			if (fieldName === "description") {
				this.update.label = this.$t(fieldName)
				this.update.componentName = "j-base-input-long-text"
				this.update.onSubmit = () => this.upsertL10n(fieldName)
				this.update.value = this.achievementType.description
			}
			if (fieldName === "type") {
				this.update.label = this.$t(fieldName)
				this.update.componentName = "j-base-input-text"
				this.update.onSubmit = () => this.updateAchievementType(fieldName)
				this.update.value = this.achievementType.type
			}
			this.$modal.show("updateFieldModal")
		},

		onInputLogic(payload) {
			this.update.value = payload
			this.updateAchievementType("logic")
		},

		async updateAchievementType(fieldName) {
			const query = gql`
				mutation updateAchievementType(
					$id: String!
					$input: updateAchievementTypeInput!
				) {
					updateAchievementType(id: $id, input: $input) {
						errors
						status
					}
				}
			`
			const res = await api({
				query,
				variables: {
					id: this.id,
					input: {
						[fieldName]: this.update.value
					}
				}
			})
			this.isLoading = false
			if (res?.data?.updateAchievementType?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.updateAchievementType?.errors[0] ||
						this.$t("errors.general")
				)
			} else {
				this.$modal.hide("updateFieldModal")
				this.fetchAchievementType()
			}
		},

		async upsertL10n(fieldName) {
			const l10nquery = gql`
				mutation upsertL10n($l10n: upsertL10nInput) {
					upsertL10n(l10n: $l10n) {
						status
						errors
					}
				}
			`
			const res = await api({
				query: l10nquery,
				variables: {
					l10n: {
						locale: this.selectedLocale,
						key: `achievements-${this.achievementType.id}-${fieldName}`,
						value: this.update.value
					}
				}
			})
			this.isLoading = false
			if (res?.data?.upsertL10n?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.upsertL10n?.errors[0] || this.$t("errors.general")
				)
			} else {
				this.$modal.hide("updateFieldModal")
				this.fetchAchievementType()
			}
		},

		async fetchAchievementType() {
			this.isLoading = true
			const query = gql`
				query achievementTypes($where: achievementTypeWhereInput) {
					achievementTypes(where: $where) {
						totalCount
						list {
							id
							name
							description
							type
							logic {
								graphQlQuery
								liquidCondition
								and {
									graphQlQuery
									liquidCondition
									and {
										graphQlQuery
										liquidCondition
										and {
											graphQlQuery
											liquidCondition
										}
										or {
											graphQlQuery
											liquidCondition
										}
									}
									or {
										graphQlQuery
										liquidCondition
										and {
											graphQlQuery
											liquidCondition
										}
										or {
											graphQlQuery
											liquidCondition
										}
									}
								}
								or {
									graphQlQuery
									liquidCondition
									and {
										graphQlQuery
										liquidCondition
										and {
											graphQlQuery
											liquidCondition
										}
										or {
											graphQlQuery
											liquidCondition
										}
									}
									or {
										graphQlQuery
										liquidCondition
										and {
											graphQlQuery
											liquidCondition
										}
										or {
											graphQlQuery
											liquidCondition
										}
									}
								}
							}
						}
					}
				}
			`
			const res = await api({
				locale: this.selectedLocale,
				query,
				variables: {
					where: { id: this.id }
				}
			})

			if (res?.data?.achievementTypes?.errors) {
				this.$store.dispatch(
					"showError",
					res?.data?.achievementTypes?.errors[0]
				)
			}

			this.isLoading = false

			this.achievementType = res?.data?.achievementTypes?.list[0] || {}
		}
	},

	created() {
		this.fetchAchievementType()
	},

	watch: {
		selectedLocale: {
			handler(o, n) {
				if (o && n) {
					this.fetchAchievementType()
				}
			}
		}
	}
}
</script>
